import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, Typography, Paper, Button, Dialog, 
  DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, IconButton, Snackbar, Alert 
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { 
  getPatternCategories, 
  createPatternCategory, 
  updatePatternCategory, 
  deletePatternCategory 
} from '../services/patternService';

const PatternCategories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // 获取样板分类数据
  const fetchCategories = async () => {
    try {
      const response = await getPatternCategories();
      if (response.success) {
        setCategories(response.data);
      } else {
        showSnackbar('获取分类列表失败', 'error');
      }
    } catch (error) {
      console.error('获取分类列表错误', error);
      showSnackbar('获取分类列表失败: ' + (error.message || '未知错误'), 'error');
    }
  };

  // 初始化加载
  useEffect(() => {
    fetchCategories();
  }, []);

  // 显示提示信息
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // 关闭提示
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 打开创建/编辑对话框
  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({ name: category.name, description: category.description || '' });
    } else {
      setEditingCategory(null);
      setFormData({ name: '', description: '' });
    }
    setOpenDialog(true);
  };

  // 关闭对话框
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // 打开删除确认对话框
  const handleOpenDeleteDialog = (category) => {
    setEditingCategory(category);
    setOpenDeleteDialog(true);
  };

  // 关闭删除确认对话框
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  // 表单输入变化处理
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // 保存分类
  const handleSaveCategory = async () => {
    try {
      if (!formData.name.trim()) {
        showSnackbar('分类名称不能为空', 'error');
        return;
      }

      let response;
      if (editingCategory) {
        // 更新
        response = await updatePatternCategory({
          id: editingCategory.id,
          ...formData
        });
      } else {
        // 创建
        response = await createPatternCategory(formData);
      }

      if (response.success) {
        showSnackbar(editingCategory ? '分类更新成功' : '分类创建成功');
        fetchCategories();
        handleCloseDialog();
      } else {
        showSnackbar(response.message || '操作失败', 'error');
      }
    } catch (error) {
      console.error('保存分类错误', error);
      showSnackbar('操作失败: ' + (error.message || '未知错误'), 'error');
    }
  };

  // 删除分类
  const handleDeleteCategory = async () => {
    try {
      if (!editingCategory) {
        return;
      }

      const response = await deletePatternCategory(editingCategory.id);
      if (response.success) {
        showSnackbar('分类删除成功');
        fetchCategories();
      } else {
        showSnackbar(response.message || '删除失败', 'error');
      }
    } catch (error) {
      console.error('删除分类错误', error);
      showSnackbar('删除失败: ' + (error.message || '未知错误'), 'error');
    } finally {
      handleCloseDeleteDialog();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        <Typography component="h2" variant="h6" color="primary" gutterBottom>
          样板分类管理
        </Typography>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          sx={{ alignSelf: 'flex-end', mb: 2 }}
          onClick={() => handleOpenDialog()}
        >
          添加分类
        </Button>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>名称</TableCell>
                <TableCell>描述</TableCell>
                <TableCell>创建时间</TableCell>
                <TableCell>更新时间</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">暂无数据</TableCell>
                </TableRow>
              ) : (
                categories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell>{category.id}</TableCell>
                    <TableCell>{category.name}</TableCell>
                    <TableCell>{category.description || '-'}</TableCell>
                    <TableCell>{new Date(category.created_at).toLocaleString()}</TableCell>
                    <TableCell>{new Date(category.updated_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        color="primary" 
                        onClick={() => handleOpenDialog(category)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => handleOpenDeleteDialog(category)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 创建/编辑对话框 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCategory ? '编辑分类' : '添加分类'}</DialogTitle>
        <DialogContent>
          <TextField
            name="name"
            label="分类名称"
            value={formData.name}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            name="description"
            label="分类描述"
            value={formData.description}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button onClick={handleSaveCategory} color="primary" variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 删除确认对话框 */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除分类 "{editingCategory?.name}" 吗？此操作不可撤销。
          </Typography>
          <Typography color="error" sx={{ mt: 1 }}>
            注意：如果分类下有样板，将无法删除。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>取消</Button>
          <Button onClick={handleDeleteCategory} color="error" variant="contained">
            删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 提示消息 */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PatternCategories;
